import { z } from 'zod';
export const InvoiceExtractionSourceSchema = z.enum(['facturx', 'text', 'ocr']);
export const FacturXProfileSchema = z.enum([
    'MINIMUM',
    'BASIC WL',
    'BASIC',
    'EN16931',
    'EXTENDED',
    'unknown',
]);
export const InvoiceDocumentTypeSchema = z.enum(['invoice', 'credit']);
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const ExtractedLineSchema = z.object({
    productId: z.string().nullable(),
    label: z.string(),
    quantity: z.number().nullable(),
    unitPriceHT: z.number().nullable(),
    vatRate: z.number().nullable(),
    lineTotalHT: z.number().nullable(),
    lineTotalTTC: z.number().nullable(),
});
export const ExtractedSellerSchema = z.object({
    name: z.string().nullable(),
    siret: z.string().nullable(),
    siren: z.string().nullable(),
    vatNumber: z.string().nullable(),
    iban: z.string().nullable(),
    bic: z.string().nullable(),
    email: z.string().nullable(),
    address: z.string().nullable(),
    postalCode: z.string().nullable(),
    city: z.string().nullable(),
});
export const ExtractedTotalsSchema = z.object({
    totalHT: z.number().nullable(),
    totalVAT: z.number().nullable(),
    totalTTC: z.number().nullable(),
    amountDue: z.number().nullable(),
});
export const InvoiceExtractionSchema = z.object({
    source: InvoiceExtractionSourceSchema,
    facturxProfile: FacturXProfileSchema.optional(),
    documentType: InvoiceDocumentTypeSchema.nullable(),
    reference: z.string().nullable(),
    issueDate: isoDate.nullable(),
    dueDate: isoDate.nullable(),
    referencedInvoiceReference: z.string().nullable(),
    seller: ExtractedSellerSchema,
    totals: ExtractedTotalsSchema,
    lines: z.array(ExtractedLineSchema),
    allowanceCharges: z.array(ExtractedLineSchema),
    rawText: z.string().nullable(),
    warnings: z.array(z.string()),
});
export const SupplierCandidateSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    siret: z.string().nullable(),
    vatNumber: z.string().nullable(),
    iban: z.string().nullable(),
});
export const SuggestedNewSupplierSchema = ExtractedSellerSchema.omit({ siren: true });
export const SupplierMatchSchema = z.object({
    supplierId: z.number().int().nullable(),
    confidence: z.enum(['exact', 'fuzzy']).nullable(),
    matchedBy: z.enum(['siret', 'siren', 'vatNumber', 'iban', 'name']).nullable(),
    suggestedNewSupplier: SuggestedNewSupplierSchema,
});
export const CatalogPrestationSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    prices: z.array(z.object({ id: z.number().int(), name: z.string(), price: z.number() })),
});
export const LineMappingSchema = z.object({
    key: z.string(),
    prestationId: z.number().int().nullable(),
    prestationPriceId: z.number().int().nullable(),
    chargeAccountCode: z.string().nullable(),
    accountingCategoryId: z.number().int().nullable(),
});
export const SupplierCatalogSchema = z.object({
    prestations: z.array(CatalogPrestationSchema),
    mappings: z.array(LineMappingSchema),
    defaultChargeAccountCode: z.string().nullable(),
});
export const LineMatchSourceSchema = z.enum([
    'mapping',
    'fuzzy-mapping',
    'prestation-name',
    'text-scan',
]);
export const SuggestedLineSchema = z.object({
    sourceKeys: z.array(z.string()),
    prestationId: z.number().int().nullable(),
    prestationPriceId: z.number().int().nullable(),
    label: z.string().nullable(),
    // Null only for text-scan lines, where the invoice text gave no usable quantity or price.
    quantity: z.number().nullable(),
    unitPrice: z.number().nullable(),
    chargeAccountCode: z.string().nullable(),
    accountingCategoryId: z.number().int().nullable(),
    priceMismatch: z.object({ tariffPrice: z.number(), invoicedUnitPrice: z.number() }).nullable(),
    matchedBy: LineMatchSourceSchema.nullable(),
});
export const LineMatchResultSchema = z.object({
    suggestedLines: z.array(SuggestedLineSchema),
    warnings: z.array(z.string()),
});
